import React, {useState} from 'react';
import {Modal, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {enqueueCommand, fetchCommands} from '@/entities/device/api';
import type {DeviceCommandRecord, DeviceSummary} from '@/entities/device/types';
import {fetchLatestRelease} from '@/entities/firmware/api';
import {
  formatSize,
  INSTALL_COMMAND,
  updateState,
  type FirmwareKind,
  type FirmwareRelease,
  type UpdateState,
} from '@/entities/firmware/types';
import {ApiError} from '@/shared/api';
import {formatTimestamp} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Field, FieldGroup} from '@/shared/ui/field';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * «Обновление ПО» — проверить, отстаёт ли терминал от реестра, и поставить новое.
 *
 * ## Две дорожки, две карточки
 *
 * Приложение (`app`, команда `ota_update`) и образ системы (`firmware`, команда
 * `firmware_update`) обновляются независимо, у каждой своя «последняя» версия и своя
 * версия на плате. Одна кнопка «обновить» на обе была бы способом отправить не то:
 * приложение ставится за секунды, образ — минуты, две перезагрузки и без отката.
 * Поэтому образ спрашивает подтверждения с перечнем последствий, а приложение —
 * коротким «точно?».
 *
 * ## Что значит «проверить»
 *
 * Сравнить версию, которую плата сообщила при последней синхронизации, с первой
 * записью реестра в канале САМОЙ ПЛАТЫ (`update_channel` / `firmware_channel`) — на
 * равенство, не на порядок, как это делает сама плата. Чужой канал сравнивать
 * нельзя: плата ставит только своё и отвергает закреплённую версию из другого. Реестр вендорский и живёт в домене `system`: владелец компании без
 * платформенной роли получает `403`. Тогда проверка честно говорит, что сверить не с
 * чем, а установка всё равно доступна — плата сама спросит реестр и поставит
 * опубликованное (пустые `params`).
 *
 * ## Версия в команде
 *
 * Если последняя версия известна, она уходит в `params.version`: плата поставит
 * ровно её или откажется, если за это время опубликовали другую. Человек
 * подтверждал конкретную версию — ставить молча соседнюю было бы нечестно.
 *
 * ## После нажатия
 *
 * Команда исполняется при следующей синхронизации. `acked` здесь значит «установка
 * началась», а не «закончилась»: установка кончается перезагрузкой, и успех
 * приходит молчанием, а потом — новой версией в карточке.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  track: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  headText: {flex: 1, gap: 2},
  pills: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  backdrop: {flex: 1, justifyContent: 'center', padding: spacing(5)},
  dialog: {borderRadius: radii.xl, padding: spacing(5), gap: spacing(4)},
  bullets: {gap: spacing(1)},
  row: {flexDirection: 'row', gap: spacing(2)},
  flex: {flex: 1},
});

const TRACKS: readonly FirmwareKind[] = ['app', 'firmware'];

/**
 * The channel the board follows on this track. The board installs only the newest
 * build of its own channel and refuses a pinned version from another one, so a
 * terminal parked on `beta` checked against `stable` would be offered a version it
 * will never install — and the pinned command would fail.
 */
function channelOf(summary: DeviceSummary, kind: FirmwareKind): string {
  const c = kind === 'app' ? summary.update_channel : summary.firmware_channel;
  return c?.trim() || 'stable';
}

const IN_FLIGHT = new Set(['pending', 'delivered']);

function stateTone(state: UpdateState): StatusTone {
  switch (state) {
    case 'current':
      return 'granted';
    case 'available':
      return 'pending';
    default:
      return 'neutral';
  }
}

function commandTone(status: string): StatusTone {
  if (status === 'acked') {
    return 'granted';
  }
  if (status === 'failed' || status === 'expired') {
    return 'denied';
  }
  return 'pending';
}

function installedOf(summary: DeviceSummary, kind: FirmwareKind): string | null {
  const v = kind === 'app' ? summary.app_version : summary.firmware_version;
  return v && v.trim() ? v : null;
}

/** The newest install command of this track in the device's history, if any. */
function lastInstall(
  commands: DeviceCommandRecord[],
  kind: FirmwareKind,
): DeviceCommandRecord | undefined {
  return commands.find(c => c.kind === INSTALL_COMMAND[kind]);
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 401);
}

export function DeviceUpdatePanel({
  companyId,
  deviceId,
  summary,
}: {
  companyId: string;
  deviceId: string;
  summary: DeviceSummary;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<FirmwareKind | null>(null);

  const appChannel = channelOf(summary, 'app');
  const imageChannel = channelOf(summary, 'firmware');
  const app = useQuery({
    queryKey: ['firmware-latest', 'app', appChannel],
    queryFn: () => fetchLatestRelease('app', appChannel),
    staleTime: 60_000,
    // 403 is an answer ("not your registry"), not a hiccup worth three retries.
    retry: (count, error) => !isForbidden(error) && count < 2,
  });
  const image = useQuery({
    queryKey: ['firmware-latest', 'firmware', imageChannel],
    queryFn: () => fetchLatestRelease('firmware', imageChannel),
    staleTime: 60_000,
    retry: (count, error) => !isForbidden(error) && count < 2,
  });
  const latest = {app, firmware: image};

  const commandsQuery = useQuery({
    queryKey: ['device-commands', companyId, deviceId],
    queryFn: () => fetchCommands(companyId, deviceId),
    // Poll only while an install is on its way to the board.
    refetchInterval: query =>
      (query.state.data?.commands ?? []).some(
        c =>
          (c.kind === INSTALL_COMMAND.app || c.kind === INSTALL_COMMAND.firmware) &&
          IN_FLIGHT.has(c.status),
      )
        ? 5000
        : false,
  });
  const commands = commandsQuery.data?.commands ?? [];

  const install = useMutation({
    meta: {toast: 'toast.deviceCommand', toastError: true},
    mutationFn: ({kind, release}: {kind: FirmwareKind; release: FirmwareRelease | null}) =>
      enqueueCommand(
        companyId,
        deviceId,
        INSTALL_COMMAND[kind],
        release ? {version: release.version} : {},
      ),
    onSuccess: () => {
      setConfirm(null);
      void qc.invalidateQueries({queryKey: ['device-commands', companyId, deviceId]});
    },
  });

  const checking = app.isFetching || image.isFetching;
  const check = () => {
    void app.refetch();
    void image.refetch();
    // The installed side of the comparison comes from the device row — re-read it too,
    // so a board that updated since the screen opened stops showing "available".
    void qc.invalidateQueries({queryKey: ['device', companyId, deviceId]});
  };

  const forbidden = isForbidden(app.error) || isForbidden(image.error);
  const failed = !forbidden && (app.isError || image.isError);

  const confirmRelease = confirm ? latest[confirm].data ?? null : null;

  return (
    <View style={styles.root}>
      <AppButton
        title={t('update.check')}
        tone="soft"
        icon="refresh"
        loading={checking}
        onPress={check}
        testID="device-update-check"
      />

      {forbidden ? (
        <Banner tone="info" title={t('update.registryForbidden')} message={t('update.registryForbiddenHint')} />
      ) : null}
      {failed ? <Banner tone="error" title={t('update.checkFailed')} /> : null}
      {!summary.online ? (
        <Banner tone="warning" title={t('update.offline')} />
      ) : null}

      {TRACKS.map(kind => {
        const q = latest[kind];
        const release = q.data;
        const installed = installedOf(summary, kind);
        // Unknown registry (403/error/loading) must not read as "unpublished".
        const state = q.isSuccess ? updateState(installed, release) : 'unknown';
        const cmd = lastInstall(commands, kind);
        const busy = !!cmd && IN_FLIGHT.has(cmd.status);
        const canInstall =
          !busy && (state === 'available' || (state === 'unknown' && release !== null));

        return (
          <Card key={kind} padding={4} style={styles.track} testID={`device-update-${kind}`}>
            <View style={styles.head}>
              <View style={styles.headText}>
                <AppText variant="heading">{t(`update.track.${kind}`)}</AppText>
                <AppText variant="caption">{t(`update.trackHint.${kind}`)}</AppText>
              </View>
            </View>

            <FieldGroup>
              <Field label={t('update.installed')} value={installed} mono />
              <Field label={t('update.channel')} value={channelOf(summary, kind)} mono />
              <Field
                label={t('update.latest')}
                value={
                  release
                    ? release.version
                    : q.isSuccess
                    ? t('update.nothingPublished')
                    : null
                }
                mono={!!release}
                note={
                  release
                    ? `${formatSize(release.size_bytes)} · ${formatTimestamp(release.published_at)}`
                    : null
                }
              />
            </FieldGroup>

            {release?.notes ? (
              <AppText variant="caption">{release.notes}</AppText>
            ) : null}

            <View style={styles.pills}>
              <StatusPill
                tone={stateTone(state)}
                size="sm"
                label={t(`update.state.${state}`)}
              />
              {cmd ? (
                <StatusPill
                  tone={commandTone(cmd.status)}
                  size="sm"
                  label={t(`update.command.${cmd.status}`, {
                    defaultValue: cmd.status,
                    at: formatTimestamp(cmd.created_at),
                  })}
                />
              ) : null}
            </View>

            {cmd?.status === 'acked' ? (
              <AppText variant="caption">{t(`update.ackedHint.${kind}`)}</AppText>
            ) : null}
            {cmd?.status === 'failed' && cmd.result_message ? (
              <Banner tone="error" title={t('update.failed')} message={cmd.result_message} />
            ) : null}

            {canInstall ? (
              <AppButton
                title={
                  release
                    ? t('update.installVersion', {version: release.version})
                    : t('update.installLatest')
                }
                tone={kind === 'firmware' ? 'outline' : 'primary'}
                icon="download"
                disabled={install.isPending}
                onPress={() => setConfirm(kind)}
                testID={`device-update-install-${kind}`}
              />
            ) : null}
          </Card>
        );
      })}

      <ConfirmInstall
        kind={confirm}
        release={confirmRelease}
        deviceName={summary.device_name}
        loading={install.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm && install.mutate({kind: confirm, release: confirmRelease})}
      />
    </View>
  );
}

function ConfirmInstall({
  kind,
  release,
  deviceName,
  loading,
  onCancel,
  onConfirm,
}: {
  kind: FirmwareKind | null;
  release: FirmwareRelease | null;
  deviceName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const image = kind === 'firmware';

  return (
    <Modal visible={kind !== null} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={[styles.backdrop, {backgroundColor: p.overlay}]}>
        <View style={[styles.dialog, {backgroundColor: p.elevated}]}>
          <AppText variant="title">
            {t(image ? 'update.confirmImageTitle' : 'update.confirmAppTitle')}
          </AppText>
          <AppText variant="body" color={p.mutedForeground}>
            {t('update.confirmBody', {
              name: deviceName,
              version: release?.version ?? t('update.latestPublished'),
            })}
          </AppText>
          {image ? (
            <Banner
              tone="warning"
              title={t('update.imageWarningTitle')}
              message={t('update.imageWarning')}
            />
          ) : (
            <View style={styles.bullets}>
              <AppText variant="caption">{t('update.appWarning')}</AppText>
            </View>
          )}
          <View style={styles.row}>
            <AppButton
              title={t('actions.cancel')}
              tone="neutral"
              style={styles.flex}
              onPress={onCancel}
            />
            <AppButton
              title={t('update.confirmAction')}
              tone={image ? 'destructive' : 'primary'}
              style={styles.flex}
              loading={loading}
              onPress={onConfirm}
              testID="device-update-confirm"
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}
